import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';
import MusicVideoIcon from '@mui/icons-material/MusicVideo';
import SmsIcon from '@mui/icons-material/Sms';
import GoogleIcon from '@mui/icons-material/Google';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const channelIcons = {
  whatsapp: WhatsAppIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  telegram: TelegramIcon,
  email: EmailIcon,
  voip: PhoneInTalkIcon,
  sip: SettingsPhoneIcon,
  tiktok: MusicVideoIcon,
  sms: SmsIcon,
  google_business: GoogleIcon,
  mercadolivre: StorefrontIcon,
  teams: GroupsIcon,
};

const channelColors = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  instagram: '#E4405F',
  telegram: '#0088CC',
  email: '#FF6B00',
  voip: '#8B5CF6',
  sip: '#06B6D4',
  tiktok: '#000000',
  sms: '#43A047',
  google_business: '#4285F4',
  mercadolivre: '#FFE600',
  teams: '#6264A7',
};

export default function HubMarketplace() {
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preselectedChannel = searchParams.get('channel');

  // Contexto assinado do redirect do HUB — precisa ser repassado ao checkout/trial
  const hubTenantId = searchParams.get('tenantId') || '';
  const hubTenantName = searchParams.get('tenantName') || '';
  const hubTs = searchParams.get('ts') || '';
  const hubSig = searchParams.get('signature') || '';
  const redirectQs = () => {
    const p = new URLSearchParams();
    if (hubTenantId) p.set('tenantId', hubTenantId);
    if (hubTenantName) p.set('tenantName', hubTenantName);
    if (hubTs) p.set('ts', hubTs);
    if (hubSig) p.set('signature', hubSig);
    return p.toString();
  };

  // ── Modo carrinho: aberto pelo HUB com channel=cart + assinatura ──
  const CONTRATAVEIS = ['whatsapp', 'facebook', 'instagram'];
  const cartMode = !!hubSig && preselectedChannel === 'cart';
  const [cart, setCart] = useState({});
  const [paying, setPaying] = useState(false);

  const setQty = (channel, delta) =>
    setCart((c) => ({ ...c, [channel]: Math.max(0, (c[channel] || 0) + delta) }));

  const unitPrice = (plan) =>
    billingCycle === 'yearly' && plan.priceYearly
      ? Number(plan.priceYearly)
      : Number(plan.priceMonthly);

  const cartTotal = () =>
    plans.reduce(
      (sum, p) => sum + (cart[p.channel] > 0 ? unitPrice(p) * cart[p.channel] : 0),
      0
    );

  const cartCount = () =>
    Object.values(cart).reduce((a, b) => a + (b || 0), 0);

  const handlePagarTudo = async () => {
    const items = Object.entries(cart)
      .filter(([, q]) => q > 0)
      .map(([channel, quantity]) => ({ channel, quantity }));
    if (items.length === 0) {
      setError('Adicione ao menos 1 conexão ao pacote.');
      return;
    }
    setPaying(true);
    setError('');
    try {
      const { data } = await api.post('/hub-subscriptions/checkout/cart', {
        tenantId: hubTenantId,
        tenantName: hubTenantName,
        ts: hubTs,
        signature: hubSig,
        channel: 'cart',
        hubTenantId: hubTenantId ? Number(hubTenantId) : 0,
        billingCycle,
        items,
        returnUrl: window.location.origin + '/hub/subscriptions',
      });
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        setError('Não foi possível iniciar o pagamento.');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Erro ao iniciar o pagamento.'
      );
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (preselectedChannel && preselectedChannel !== 'cart' && plans.length > 0) {
      const plan = plans.find((p) => p.channel === preselectedChannel);
      if (plan && !isSubscribed(preselectedChannel)) {
        navigate(`/hub/checkout/${preselectedChannel}?${redirectQs()}`);
      }
    }
  }, [preselectedChannel, plans, navigate]);

  const loadData = async () => {
    try {
      // my-subscriptions é POST (protegido por RedirectSignatureGuard);
      // só faz a chamada se temos contexto assinado do HUB.
      const subsPromise = hubSig
        ? api
            .post('/hub-subscriptions/my-subscriptions', {
              tenantId: hubTenantId,
              tenantName: hubTenantName,
              ts: hubTs,
              signature: hubSig,
            })
            .catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const [plansRes, subsRes] = await Promise.all([
        api.get('/hub-subscriptions/plans'),
        subsPromise,
      ]);
      setPlans(plansRes.data);
      setSubscriptions(subsRes.data);
    } catch (err) {
      setError('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = (channel) => {
    return subscriptions.some(
      (s) => s.channel === channel && s.status === 'active'
    );
  };

  const hasUsedTrial = (channel) => {
    return subscriptions.some(
      (s) => s.channel === channel && s.isTrial
    );
  };

  const [trialLoading, setTrialLoading] = useState(null);

  const handleStartTrial = async (plan) => {
    setTrialLoading(plan.channel);
    try {
      await api.post('/hub-subscriptions/trial/start', {
        // contexto assinado do redirect (o RedirectSignatureGuard valida isto)
        tenantId: hubTenantId,
        tenantName: hubTenantName,
        ts: hubTs,
        signature: hubSig,
        channel: plan.channel,
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao iniciar teste gratuito');
    } finally {
      setTrialLoading(null);
    }
  };

  // Entrada secreta na administração: 5 cliques no logo (dentro de 1.5s cada)
  const logoClicks = useRef({ count: 0, timer: null });
  const handleLogoClick = () => {
    const lc = logoClicks.current;
    lc.count += 1;
    if (lc.timer) clearTimeout(lc.timer);
    if (lc.count >= 5) {
      lc.count = 0;
      navigate('/login-admin');
      return;
    }
    lc.timer = setTimeout(() => { lc.count = 0; }, 1500);
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header compacto */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            src={`${process.env.PUBLIC_URL}/iconeHUB.png`}
            alt="OtimizApp"
            onClick={handleLogoClick}
            style={{ height: 40, width: 40, objectFit: 'contain', display: 'block', userSelect: 'none' }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              OtimizApp Marketplace
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.1 }}>
              Hub de Canais
            </Typography>
          </Box>
        </Box>
        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={(_, val) => val && setBillingCycle(val)}
          size="small"
        >
          <ToggleButton value="monthly" sx={{ px: 1.5, py: 0.3, fontSize: '0.75rem' }}>Mensal</ToggleButton>
          <ToggleButton value="yearly" sx={{ px: 1.5, py: 0.3, fontSize: '0.75rem' }}>
            Anual
            <Chip label="-17%" size="small" color="success" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        {cartMode
          ? 'Monte seu pacote: ajuste a quantidade de conexões por canal e pague tudo de uma vez.'
          : 'Catálogo de canais. A contratação é feita pelo superAdmin em Configurações → Infraestrutura → OtimizApp HUB → Contratar canais.'}
      </Alert>

      {/* Grid 3x2 */}
      <Grid container spacing={2}>
        {plans.map((plan) => {
          const Icon = channelIcons[plan.channel] || EmailIcon;
          const color = channelColors[plan.channel] || '#FF6B00';
          const subscribed = isSubscribed(plan.channel);
          const perMonth =
            billingCycle === 'yearly' && plan.priceYearly
              ? plan.priceYearly / 12
              : plan.priceMonthly;

          return (
            <Grid item xs={6} sm={4} key={plan.id}>
              <Card
                sx={{
                  position: 'relative',
                  border: subscribed ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
              >
                {subscribed ? (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Ativo"
                    color="success"
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, height: 22, fontSize: '0.7rem' }}
                  />
                ) : plan.trialDays > 0 && !hasUsedTrial(plan.channel) ? (
                  <Chip
                    label={`${plan.trialDays} dia gratis`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(139, 92, 246, 0.12)',
                      color: '#7C3AED',
                    }}
                  />
                ) : null}

                <CardContent sx={{ textAlign: 'center', p: 2.5, pb: '16px !important' }}>
                  <Icon sx={{ fontSize: 40, color, mb: 0.5 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
                    {plan.name}
                  </Typography>

                  {/* Preco */}
                  <Typography variant="h6" sx={{ fontWeight: 700, color, mt: 0.5, fontSize: '1.2rem' }}>
                    {formatPrice(perMonth)}
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      /mes
                    </Typography>
                  </Typography>

                  {/* Features */}
                  <Box sx={{ textAlign: 'left', mt: 1, mb: 1.5 }}>
                    {(plan.features || []).slice(0, 3).map((feature, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>{feature}</Typography>
                      </Box>
                    ))}
                    {(plan.features || []).length > 3 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 2.5 }}>
                        +{plan.features.length - 3} mais
                      </Typography>
                    )}
                  </Box>

                  {cartMode && CONTRATAVEIS.includes(plan.channel) ? (
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => setQty(plan.channel, -1)} sx={{ minWidth: 34, px: 0, fontWeight: 700 }}>−</Button>
                      <Typography sx={{ fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{cart[plan.channel] || 0}</Typography>
                      <Button size="small" variant="contained" onClick={() => setQty(plan.channel, 1)} sx={{ minWidth: 34, px: 0, fontWeight: 700, bgcolor: color, '&:hover': { bgcolor: color } }}>+</Button>
                    </Box>
                  ) : cartMode ? (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block', mt: 0.5 }}>
                      Em breve
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        mt: 0.5,
                        py: 0.9,
                        px: 1,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(107,72,255,0.07)',
                        border: '1px dashed rgba(107,72,255,0.35)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#6B48FF', fontWeight: 700, display: 'block', lineHeight: 1.3 }}>
                        Contrate pelo HUB
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.2 }}>
                        Contrate em Infraestrutura → OtimizApp HUB (superAdmin).
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {cartMode && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 2,
            py: 1.5,
            px: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {cartCount()} {cartCount() === 1 ? 'conexão' : 'conexões'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {formatPrice(cartTotal())}
              <Typography component="span" variant="caption" color="text.secondary">
                {billingCycle === 'yearly' ? '/ano' : '/mês'}
              </Typography>
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            disabled={paying || cartCount() === 0}
            onClick={handlePagarTudo}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' }, fontWeight: 700, textTransform: 'none' }}
          >
            {paying ? 'Processando...' : 'Pagar tudo'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
